import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { STYLES } from "./ShoppingCartStyles";
import { CartItem } from "@ecommerce/store";
import { numberFormatter } from "@ecommerce/shared-utils";
import { AppConfigFields } from "../../types/appConfigEntities";
import { Icon } from "../liquid/LiquidPrimitives";

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

interface ShoppingCartItemProps {
    item: CartItem;
    onUpdateQuantity: (idProduct: string, idProductVariant: string, quantity: number) => void;
    onRemoveItem: (idProduct: string, idProductVariant: string) => void;
}

export const ShoppingCartItem: React.FC<ShoppingCartItemProps> = ({
    item,
    onUpdateQuantity,
    onRemoveItem,
}) => {
    const effectiveMinQuantity = minimumPurchase ?? 1;
    const stock = item.stock ?? 999;
    const isPurchasable = stock >= effectiveMinQuantity;

    const [inputValue, setInputValue] = useState<string>(item.quantity.toString());

    useEffect(() => {
        setInputValue(item.quantity.toString());
    }, [item.quantity]);

    const handleIncrement = () => {
        if (!isPurchasable) return;
        const next = Math.min(item.quantity + 1, stock);
        onUpdateQuantity(item.idProduct, item.idProductVariant, next);
    };

    const handleDecrement = () => {
        const next = Math.max(item.quantity - 1, effectiveMinQuantity);
        onUpdateQuantity(item.idProduct, item.idProductVariant, next);
    };

    const canDecrement = item.quantity > effectiveMinQuantity;
    const canIncrement = isPurchasable && item.quantity < stock;

    const btnStyle: React.CSSProperties = {
        width: 28,
        height: 28,
        borderRadius: 999,
        border: "none",
        background: "white",
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    };

    return (
        <Box
            sx={{
                mb: 2.5,
                pb: 2.5,
                borderBottom: "1px solid var(--divider)",
                "&:last-child": { borderBottom: "none", mb: 0, pb: 0 },
            }}
        >
            <Box sx={{ display: "flex", gap: 1.5 }}>
                {/* Imagen */}
                <Box
                    component="img"
                    src={item.image}
                    alt={item.name}
                    sx={{
                        width: 72,
                        height: 96,
                        objectFit: "cover",
                        borderRadius: "10px",
                        flexShrink: 0,
                    }}
                />

                {/* Info del producto */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: STYLES.fontFamily,
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#1a1c1e",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            lineHeight: 1.3,
                            mb: 0.5,
                            pr: 3,
                        }}
                    >
                        {item.name}
                    </Typography>

                    {item.attributes && item.attributes.length > 0 && (
                        <Typography
                            sx={{
                                fontFamily: STYLES.fontFamily,
                                fontSize: "11px",
                                color: "#707882",
                                mb: 0.75,
                                textTransform: "capitalize",
                            }}
                        >
                            {item.attributes.map((a) => a.value).join(", ")}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            fontFamily: "'SF Mono', 'Fira Code', monospace",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#1a1c1e",
                            letterSpacing: "-0.02em",
                            mb: 1,
                        }}
                    >
                        {numberFormatter.toStringWithAmount(item.price, "$")}
                    </Typography>

                    {/* Control de cantidad */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "3px",
                            borderRadius: 999,
                            background: "rgba(10,10,10,0.06)",
                            width: "fit-content",
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleDecrement}
                            disabled={!canDecrement}
                            style={{
                                ...btnStyle,
                                cursor: canDecrement ? "pointer" : "not-allowed",
                                opacity: canDecrement ? 1 : 0.4,
                            }}
                        >
                            <Icon.minus />
                        </button>
                        <span
                            style={{
                                fontFamily: "ui-monospace, 'SF Mono', monospace",
                                fontSize: 13,
                                fontWeight: 600,
                                minWidth: 22,
                                textAlign: "center",
                                color: "#1a1c1e",
                            }}
                        >
                            {item.quantity}
                        </span>
                        <button
                            type="button"
                            onClick={handleIncrement}
                            disabled={!canIncrement}
                            style={{
                                ...btnStyle,
                                cursor: canIncrement ? "pointer" : "not-allowed",
                                opacity: canIncrement ? 1 : 0.4,
                            }}
                        >
                            <Icon.plus />
                        </button>
                    </div>

                    {minimumPurchase > 1 && (
                        <Typography
                            sx={{
                                fontFamily: STYLES.fontFamily,
                                fontSize: "10px",
                                color: "#707882",
                                mt: 0.75,
                            }}
                        >
                            Mínimo {minimumPurchase} unidad{minimumPurchase > 1 ? "es" : ""}
                        </Typography>
                    )}
                </Box>

                {/* Botón eliminar */}
                <button
                    type="button"
                    onClick={() => onRemoveItem(item.idProduct, item.idProductVariant)}
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: "none",
                        background: "rgba(26,28,30,0.07)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        color: "#707882",
                    }}
                >
                    <Icon.trash />
                </button>
            </Box>
        </Box>
    );
};
